import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Heart, HeartPulse, Clock, UserPlus, FileText, Building2, Check, ChevronDown, ChevronUp, X, ArrowRight, PlayCircle, CalendarCheck, Eye, Bell } from 'lucide-react';
import { useOnboardingProgress, type OnboardingStep } from '@/hooks/useOnboardingProgress';
import { InviteTokenModal } from './InviteTokenModal';
import { track } from '@/lib/analytics';

const ICONS: Record<string, any> = { Heart, HeartPulse, Clock, UserPlus, FileText, Building2, CalendarCheck, Eye, Bell };

const DISMISS_KEY = 'careos_onboarding_dismissed';

interface OnboardingChecklistProps {
  /** When true, only renders while required steps are pending (for use outside Home). */
  requiredOnly?: boolean;
}

export function OnboardingChecklist({ requiredOnly = false }: OnboardingChecklistProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state, isLoading } = useOnboardingProgress();
  const [collapsed, setCollapsed] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [dismissed, setDismissed] = useState<boolean>(() => localStorage.getItem(DISMISS_KEY) === '1');

  // Emit onboarding_step_completed once per step transition (pending -> done).
  const seenDoneRef = useRef<Set<string> | null>(null);
  useEffect(() => {
    if (!state || state.steps.length === 0) return;
    const doneIds = state.steps.filter(s => s.done).map(s => s.id);
    if (seenDoneRef.current === null) {
      seenDoneRef.current = new Set(doneIds);
      return;
    }
    doneIds.forEach(id => {
      if (!seenDoneRef.current!.has(id)) {
        seenDoneRef.current!.add(id);
        const step = state.steps.find(s => s.id === id);
        track('onboarding_step_completed', {
          step_id: id,
          persona: state.persona,
          optional: !!step?.optional,
          completed: state.completed,
          total: state.total,
        });
      }
    });
  }, [state]);

  if (isLoading || !state || state.persona === 'unknown' || state.steps.length === 0) return null;
  // Experienced users (already started shifts / executed tasks) and users who
  // finished every step never see the getting-started card again.
  if (state.hasRealUsage || state.allDone) return null;
  if (dismissed) return null;
  if (requiredOnly && state.requiredDone) return null;


  const pct = state.total > 0 ? Math.round((state.completed / state.total) * 100) : 0;
  const allDone = state.allDone;
  const nextStep = state.nextStep;

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  };

  const goToStep = (step: OnboardingStep) => {
    track('cta_clicked', { cta: 'onboarding_step', step_id: step.id, persona: state.persona });
    if (step.cta === 'invite-modal') setInviteOpen(true);
    else navigate(step.cta);
  };

  return (
    <>
      <Card className="mb-6 border-primary/40 bg-gradient-to-br from-primary/5 to-transparent">
        <CardContent className="p-4">
          <div className="flex items-center justify-between gap-2 mb-3">
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-base flex items-center gap-2">
                <PlayCircle className="w-5 h-5 text-primary" />
                {allDone ? t('onboarding.allDoneTitle') : t('onboarding.title')}
              </h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                {allDone ? t('onboarding.allDoneDesc') : t('onboarding.subtitle', { done: state.completed, total: state.total })}
              </p>
            </div>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setCollapsed(c => !c)} aria-label="toggle">
                {collapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
              </Button>
              {allDone && (
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleDismiss} aria-label="dismiss">
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>

          <Progress value={pct} className="h-2 mb-3" />

          {!state.requiredDone && nextStep && (
            <div className="rounded-lg bg-muted/60 border border-border p-3 mb-3 flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">{t('onboarding.nextStep')}</p>
                <p className="text-sm font-medium truncate">{t(nextStep.titleKey)}</p>
              </div>
              <Button size="sm" className="shrink-0" onClick={() => goToStep(nextStep)}>
                {t('onboarding.continue')}
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          )}

          {state.readyForFirstShift && state.firstPatientId && (
            <div className="rounded-lg bg-primary/10 border border-primary/30 p-3 mb-3 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 text-sm font-medium text-primary">
                <Check className="w-4 h-4" />
                {t('onboarding.readyBanner')}
              </div>
              <Button size="sm" onClick={() => navigate(`/app/patients/${state.firstPatientId}`)}>
                {t('onboarding.startShift')}
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          )}

          {!collapsed && (
            <ul className="space-y-2">
              {state.steps.map(step => {
                const Icon = ICONS[step.icon] ?? Heart;
                return (
                  <li key={step.id} className="flex items-center gap-3 p-2 rounded-md hover:bg-muted/50 transition-colors">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${step.done ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
                      {step.done ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium truncate ${step.done ? 'line-through text-muted-foreground' : ''}`}>
                        {t(step.titleKey)}
                        {step.optional && <span className="ml-1 text-xs text-muted-foreground">({t('onboarding.optional')})</span>}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">{t(step.descKey)}</p>
                    </div>
                    {!step.done && (
                      <Button size="sm" variant="outline" onClick={() => goToStep(step)}>
                        {t('onboarding.configure')}
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>
      <InviteTokenModal open={inviteOpen} onOpenChange={setInviteOpen} />
    </>
  );
}
